/** Editor session controller: input, selection, drag gestures, history, and lifecycle.
 * HTML controls: chrome.ts. Canvas overlays: draw.ts. Persistence: levels/storage.ts.
 * Each session edits a private course copy and borrows the app's renderer.
 */

import {
  cloneLevelData,
  normalizeLevelData,
  saveCourse,
  resolveSaveName,
  MAX_NAME,
  NEW_COURSE_NAME,
  THEMES,
  type CourseSource,
} from '../levels/storage'
import { Camera } from '../core/camera'
import { Input } from '../core/input'
import { DT, TILE, VIEW_H, VIEW_W } from '../engine/constants'
import { EditorChrome } from './chrome'
import type { Renderer3D } from '../engine3d/scene'
import { Level, emptyLevel } from '../game/level'
import type { PlacedObject } from '../game/level'
import { OBJECT_BY_ID, type ObjectDef } from '../game/objects'
import { Terrain, type LevelData, type PaletteCategory } from '../game/types'
import { entryForSlot, paletteEntries, type PaletteEntry } from './palette'
import {
  TOOL_DEFS,
  TOOL_FOR_TERRAIN,
  fillRect,
  fits,
  lineTiles,
  moveObject,
  newToolContext,
  resizedPlacement,
  objectAtTile,
  placementIndexOf,
  terrainLabel,
  toolById,
  validateLevel,
  type Issue,
  type Tool,
  type ToolContext,
} from './tools'
import {
  MAX_CHARS,
  clipText,
  drawOverlay,
  drawTextCentre,
  drawToast,
  fillRect as fillBox,
  payloadBadges,
  type CursorBox,
  type OverlayView,
} from './draw'

import type {
  ChromeSource,
  EditorOptions,
  EditorSave,
  SavedCourse,
} from './types'
import { VIEW, type Rect } from './viewport'

/**
 * The same view, expressed in the renderer's own coordinates. The editor
 * translates the context by `VIEW` before calling `renderLevel`, so world
 * positions map to `world - camera` exactly as the renderer expects.
 */
const VIEW_LOCAL: Rect = { x: 0, y: 0, w: VIEW.w, h: VIEW.h }

/** Undo/redo entries kept. Deep copies of a 160x30 course, so keep it sane. */
const HISTORY_LIMIT = 50

// ── drag state ─────────────────────────────────────────────────────────────

type DragMode = 'paint' | 'fill' | 'move'

interface DragState {
  pointerId: number
  mode: DragMode
  toolId: string
  startTx: number
  startTy: number
  lastTx: number
  lastTy: number
  curTx: number
  curTy: number
  /** Tile the object being moved came from. */
  originTx: number
  originTy: number
  moveIndex: number
  moveObj: PlacedObject | null
  moveW: number
  moveH: number
}

// ── editor ─────────────────────────────────────────────────────────────────

export class Editor {
  /** The surface the pointer actually lands on; may not be the draw target. */
  private readonly inputCanvas: HTMLCanvasElement
  private readonly ctx: CanvasRenderingContext2D
  private readonly onClose: () => void
  private readonly renderer3d: Renderer3D
  private readonly cam = new Camera()
  private input = new Input()
  private detachInput: (() => void) | null = null

  private level: Level
  private readonly brush: ToolContext
  private tool: Tool
  private category: PaletteCategory = 'terrain'
  private selectedKey: string | null = `t:${Terrain.GROUND}`

  private hoverTile = { tx: 0, ty: 0, inside: false }
  private drag: DragState | null = null

  private readonly undoStack: LevelData[] = []
  private readonly redoStack: LevelData[] = []
  private strokeOpen = false
  /** Taken when a stroke opens, pushed when it turns out to have changed one. */
  private openSnapshot: LevelData | null = null

  private dirty = true
  private issues: Issue[] = []
  private nameEdit: string | null = null
  private toast: { text: string; until: number } | null = null
  private now = 0
  private raf = 0
  private disposed = false
  private suspended = false
  private chrome: EditorChrome | null = null
  private onPlay: EditorOptions['onPlay']
  private onWatch: EditorOptions['onWatch']
  private handTool = false
  private selectedIndex: number | null = null
  private resizeDrag: {
    pointerId: number
    x: number
    y: number
    size: number
    axis: 'width' | 'height'
  } | null = null
  private panDrag: {
    x: number
    y: number
    camX: number
    camY: number
    pointerId: number
  } | null = null
  private previousTouchAction = ''
  /**
   * Live touch pointers on the canvas, keyed by id, for the two-finger pan.
   *
   * Single-finger drags are the brush; a second finger is the pan gesture, the
   * same split every touch drawing app uses. Tracking positions (not just
   * counts) is what lets the pan follow the midpoint, so the view moves with
   * the fingers even when one of them is holding still.
   */
  private readonly touchPointers = new Map<number, { x: number; y: number }>()
  private touchPan: {
    camX: number
    camY: number
    midX: number
    midY: number
  } | null = null
  /** When the current stroke started, so a pan can undo an accidental dot. */
  private strokeStartedAt = 0
  /**
   * The library entry this session started from, re-anchored to whatever the
   * first save landed under. See `resolveSaveName` for why a built-in forks
   * into a copy instead of overwriting.
   */
  private source: CourseSource | null
  private readonly onSave: ((save: SavedCourse) => void) | undefined
  /** Serialised level as last saved, so "no changes" is provable. */
  private savedJson: string
  /** Retry unchanged session-only saves when storage becomes available. */
  private savePending = false

  constructor(
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D,
    onClose: () => void,
    options: EditorOptions,
  ) {
    this.inputCanvas = resolveInputCanvas(canvas)
    this.ctx = ctx
    this.onClose = onClose
    this.renderer3d = options.worldRenderer
    // `normalizeLevelData` plus `cloneLevelData` means the session works on its
    // own copy from the first frame. That is what lets the shell hand us a
    // course straight out of the library, or a built-in, without the two ever
    // sharing a tile array.
    const initial =
      normalizeLevelData(options.initialLevel ?? emptyLevel(NEW_COURSE_NAME)) ??
      emptyLevel(NEW_COURSE_NAME)
    this.level = new Level(cloneLevelData(initial), {})
    this.source = options.source ?? null
    this.onSave = options.onSave
    this.onPlay = options.onPlay
    this.onWatch = options.onWatch
    this.savedJson = this.fingerprint()
    this.brush = newToolContext()
    this.brush.objectDef = OBJECT_BY_ID.get('coin') ?? null
    this.tool = toolById('ground')
    this.cam.setBounds(this.level.width, this.level.height)
    this.centerOn(this.level.data.startX, this.level.data.startY)
    this.issues = validateLevel(this.level)
  }

  // ── lifecycle ─────────────────────────────────────────────────────────

  start(): void {
    this.detachInput = this.input.attach(window)
    this.previousTouchAction = this.inputCanvas.style.touchAction
    // Stop the page scrolling / zooming under a drag.
    this.inputCanvas.style.touchAction = 'none'
    this.exitPointerLock()
    this.attachDom()
    this.chrome = new EditorChrome({
      category: (category) => {
        this.handTool = false
        this.selectCategory(category)
      },
      part: (entry) => {
        this.handTool = false
        this.selectEntry(entry)
      },
      tool: (id) => {
        this.handTool = id === 'hand'
        if (this.handTool) return
        if (id === 'paint') {
          const entry = this.selectedEntry()
          if (entry) this.selectEntry(entry)
        } else this.activateTool(toolById(id))
      },
      resize: (delta) => {
        const object = this.selectedObject()
        if (!object?.def.resize) return
        this.resizeSelected(
          (object.def.resize === 'height' ? object.def.h : object.def.w) +
            delta,
        )
        this.endStroke()
      },
      remove: () => {
        const object = this.selectedObject()
        if (!object) return
        this.ensureStroke()
        this.level.removeObject(placementIndexOf(this.level, object))
        this.selectedIndex = null
        this.endStroke()
        this.dirty = true
      },
      undo: () => this.undo(),
      redo: () => this.redo(),
      save: () => {
        const save = this.persist()
        this.reportSave(save)
        this.toastSave(save)
      },
      close: () => this.teardown(),
      play: () => {
        // Re-check so a just-edited course is not judged on a stale issues list.
        this.issues = validateLevel(this.level)
        const issue = this.issues[0]
        this.suspend()
        this.onPlay?.(
          this.snapshot(),
          issue === undefined ? undefined : `${issue.text} — PLAYING ANYWAY`,
        )
      },
      watch: () => {
        this.suspend()
        this.onWatch?.(this.snapshot())
      },
      theme: () => this.cycleTheme(1),
      rename: () => this.beginRename(),
      home: () => this.centerOn(this.level.data.startX, this.level.data.startY),
    })
    this.raf = requestAnimationFrame(this.frame)
  }

  suspend(): void {
    if (this.disposed || this.suspended) return
    if (this.drag !== null) this.endDrag(true)
    this.abandonDrag()
    this.endStroke()
    this.suspended = true
    cancelAnimationFrame(this.raf)
    this.raf = 0
    this.detachInput?.()
    this.detachInput = null
    this.detachDom()
    if (this.chrome) this.chrome.root.hidden = true
    document.getElementById('stage')?.classList.remove('editing')
    this.inputCanvas.style.touchAction = this.previousTouchAction
  }

  resume(): void {
    if (this.disposed || !this.suspended) return
    this.suspended = false
    this.input = new Input()
    this.detachInput = this.input.attach(window)
    this.attachDom()
    this.inputCanvas.style.touchAction = 'none'
    if (this.chrome) this.chrome.root.hidden = false
    document.getElementById('stage')?.classList.add('editing')
    this.raf = requestAnimationFrame(this.frame)
  }

  /** Idempotent: safe to call from the app's toggle and from Escape. */
  teardown(): void {
    if (this.disposed) return
    this.disposed = true
    this.chrome?.dispose()
    this.chrome = null
    /**
     * Abandon any open drag *first*, before the listeners come off.
     *
     * This is the leak that made the cursor feel broken. `inputCanvas` is the
     * shared game canvas, so a capture left behind outlives the editor: the
     * browser keeps routing that pointer at the canvas with no listeners
     * attached, hover state never settles, and the next click in the game is
     * swallowed. Closing with `Escape` or `E` while still holding the button was
     * enough to trigger it.
     *
     * It was also corrupting undo, which is the worse half. `this.drag` stayed
     * non-null, so `endStroke` never ran and the *next* stroke coalesced into the
     * abandoned one -- one `Ctrl+Z` then removed both.
     *
     * Abandoning rather than committing is deliberate: the paint already
     * happened and is about to be saved, so dropping the undo record loses
     * nothing a player could still reach.
     */
    try {
      this.abandonDrag()
    } catch (e) {
      console.warn('[editor] drag abandon on close failed', e)
    }
    try {
      this.reportSave(this.persist())
    } catch (e) {
      console.warn('[editor] save on close failed', e)
    }
    try {
      this.detachInput?.()
    } catch (e) {
      console.warn('[editor] input detach failed', e)
    }
    this.detachInput = null
    try {
      this.detachDom()
    } catch (e) {
      console.warn('[editor] dom detach failed', e)
    }
    if (this.raf !== 0) {
      cancelAnimationFrame(this.raf)
      this.raf = 0
    }
    this.inputCanvas.style.touchAction = this.previousTouchAction
    try {
      this.onClose()
    } catch (e) {
      console.warn('[editor] onClose threw', e)
    }
  }

  /**
   * The level as JSON, for change detection.
   *
   * Cheaper to be wrong about than a dirty flag: `dirty` is set by half a dozen
   * unrelated paths (including ones that only rebuild an identical level), and
   * a missed save or a spurious one would both be invisible. Comparing the
   * serialised level on close is exact, and costs well under a millisecond for
   * a 160x30 course.
   */
  private fingerprint(): string {
    return JSON.stringify(this.level.toData())
  }

  /**
   * Write the level out under a name the library can hold.
   *
   * A level that has not been touched is not saved. That is not just tidiness:
   * the editor is reachable from the title screen, where opening it and hitting
   * Escape used to leave a blank "My Course" in the selector every single time.
   */
  private persist(): EditorSave {
    const data = this.level.toData()
    if (!this.savePending && JSON.stringify(data) === this.savedJson) {
      return { name: null, renamed: false, durable: true }
    }
    const name = resolveSaveName(data.name, this.source)
    const durable = saveCourse({ ...data, name })
    this.savePending = !durable
    // Whatever the level is called on screen, from here on it *is* the entry we
    // just wrote. Without this, saving twice in one session (a rename, then
    // the close) would fork a second copy of the same course.
    this.source = { name, builtIn: false }
    this.level.data.name = name
    this.savedJson = this.fingerprint()
    return { name, renamed: name !== data.name, durable }
  }

  /** Hand a save result to the shell, in a way a throwing listener survives. */
  private reportSave(save: EditorSave): void {
    // Nothing was written, so there is nothing to report.
    if (save.name === null) return
    const written: SavedCourse = { ...save, name: save.name }
    try {
      this.onSave?.(written)
    } catch (e) {
      console.warn('[editor] onSave threw', e)
    }
  }

  /**
   * Say what this session's Save / Rename actually wrote.
   *
   * Same wording as the shell's close-time announce: the typed name is not
   * always the stored one (`resolveSaveName` may fork), and a full or denied
   * store is session-only.
   */
  private toastSave(save: EditorSave): void {
    if (save.name === null) return
    if (!save.durable) this.toastMsg(clip(`${save.name} - SESSION ONLY`))
    else if (save.renamed)
      this.toastMsg(clip(`SAVED AS ${save.name.toUpperCase()}`))
    else this.toastMsg(clip(`SAVED ${save.name.toUpperCase()}`))
  }

  private frame = (now: number): void => {
    if (this.disposed || this.suspended) return
    this.raf = requestAnimationFrame(this.frame)
    this.now = now
    this.input.beginFrame()
    try {
      this.handleKeys()
      this.panCamera()
      // The editor never steps the simulation, but the renderer animates off
      // `level.tick`: coins spin, saws turn and the placement cursor's dashes
      // march. Advancing it here keeps the preview alive.
      this.level.tick++
      if (this.dirty) {
        this.issues = validateLevel(this.level)
        this.dirty = false
      }
    } catch (e) {
      console.warn('[editor] update failed', e)
    }
    this.input.endFrame()
    try {
      this.render()
    } catch (e) {
      console.warn('[editor] render failed', e)
    }
  }

  // ── history ───────────────────────────────────────────────────────────

  /**
   * The level as an independent copy, for the history.
   *
   * Through `toData()`, not `level.data`. Those look interchangeable and are
   * not: `Level` copies its `tiles` into a `Uint8Array` at construction and
   * writes terrain there for the rest of its life, so `level.data.tiles` is a
   * fossil of the moment the level was built. `toData()` is the level's own
   * answer to "what is in you", and it is the only one that is current.
   *
   * Snapshotting `level.data` instead meant every undo entry carried the
   * terrain from whenever the level was last rebuilt, which cost three things
   * at once: one undo reverted *every* terrain stroke since then, redo restored
   * nothing at all (it put the same fossil back), and changing the theme --
   * which rebuilds the level from a snapshot -- silently deleted all the
   * terrain that had been painted.
   *
   * This is the "declared in one place, consumed in another" trap with the
   * serialiser on the wrong side of it. `toData()` is the declaration; read it.
   */
  private snapshot(): LevelData {
    return cloneLevelData(this.level.toData())
  }

  private pushCapped(stack: LevelData[], snap: LevelData): void {
    stack.push(snap)
    while (stack.length > HISTORY_LIMIT) stack.shift()
  }

  /**
   * Open an undo step. Called immediately *before* a mutation, and only once
   * per stroke: a drag that paints 40 tiles is one entry, because
   * `strokeOpen` is set here and cleared when the pointer goes up.
   *
   * The snapshot is *held*, not pushed. A stroke that turns out to change
   * nothing -- dragging the terrain eraser over empty air, repainting a tile
   * the brush already covers -- must not cost an undo step, because the
   * alternative is that fifty idle clicks cost the player their whole history.
   * Holding the snapshot until `endStroke` is what makes that promise real, and
   * `endStroke` is the only place that can tell whether anything changed.
   *
   * The redo stack is cleared there too, for the same reason: a stroke that was
   * not an edit has no business destroying one.
   */
  private ensureStroke(): void {
    if (this.strokeOpen) return
    this.openSnapshot = this.snapshot()
    this.strokeOpen = true
  }

  /**
   * Close the stroke, recording it only if it changed something.
   *
   * One comparison per gesture, over a structure the editor already serialises
   * on close, so the cost is the price of a save the player was going to get
   * anyway.
   */
  private endStroke(): void {
    this.strokeOpen = false
    const snap = this.openSnapshot
    this.openSnapshot = null
    if (snap === null) return
    if (sameLevel(snap, this.level)) return
    this.pushCapped(this.undoStack, snap)
    this.redoStack.length = 0
  }

  private undo(): void {
    const prev = this.undoStack.pop()
    if (prev === undefined) {
      this.toastMsg('NOTHING TO UNDO')
      return
    }
    this.pushCapped(this.redoStack, this.snapshot())
    this.rebuild(prev)
    this.dropStroke()
    this.toastMsg('UNDO')
  }

  private redo(): void {
    const next = this.redoStack.pop()
    if (next === undefined) {
      this.toastMsg('NOTHING TO REDO')
      return
    }
    this.pushCapped(this.undoStack, this.snapshot())
    this.rebuild(next)
    this.dropStroke()
    this.toastMsg('REDO')
  }

  /**
   * Abandon a stroke in progress without recording it.
   *
   * Undo and redo swap the whole level out from under an open stroke, so the
   * snapshot that stroke was holding describes a course that no longer exists.
   */
  private dropStroke(): void {
    this.strokeOpen = false
    this.openSnapshot = null
  }

  /** Swap in a different level, keeping the camera legal. */
  private rebuild(data: LevelData): void {
    this.level = new Level(cloneLevelData(data), {})
    this.cam.setBounds(this.level.width, this.level.height)
    this.clampCamera()
    this.dirty = true
  }

  // ── camera ────────────────────────────────────────────────────────────

  private centerOn(tx: number, ty: number): void {
    this.cam.x = Math.round(tx * TILE - VIEW.w / 2)
    this.cam.y = Math.round(ty * TILE - VIEW.h / 2)
    this.clampCamera()
  }

  private clampCamera(): void {
    this.cam.x = clamp(
      this.cam.x,
      0,
      Math.max(0, this.level.width * TILE - VIEW.w),
    )
    this.cam.y = clamp(
      this.cam.y,
      0,
      Math.max(0, this.level.height * TILE - VIEW.h),
    )
  }

  private panCamera(): void {
    const input = this.input
    let dx = 0
    let dy = 0
    if (input.isDown('left')) dx -= 1
    if (input.isDown('right')) dx += 1
    if (input.isDown('up')) dy -= 1
    if (input.isDown('down')) dy += 1
    if (dx === 0 && dy === 0) return
    const fast = input.codeDown('ShiftLeft') || input.codeDown('ShiftRight')
    const speed = (fast ? 480 : 200) * DT
    this.cam.x = Math.round(this.cam.x + dx * speed)
    this.cam.y = Math.round(this.cam.y + dy * speed)
    this.clampCamera()
  }

  // ── keyboard ──────────────────────────────────────────────────────────

  private handleKeys(): void {
    const input = this.input
    if (this.nameEdit !== null) return
    if (this.disposed) return

    const ctrl =
      input.codeDown('ControlLeft') ||
      input.codeDown('ControlRight') ||
      input.codeDown('MetaLeft') ||
      input.codeDown('MetaRight')

    if (ctrl) {
      if (input.codePressed('KeyZ')) {
        if (input.codeDown('ShiftLeft') || input.codeDown('ShiftRight'))
          this.redo()
        else this.undo()
        return
      }
      if (input.codePressed('KeyY')) {
        this.redo()
        return
      }
      return
    }

    if (input.codePressed('Escape') || input.codePressed('KeyE')) {
      this.teardown()
      return
    }

    for (let slot = 1; slot <= 9; slot++) {
      if (input.codePressed(`Digit${slot}`)) {
        this.pickSlot(slot)
        return
      }
    }

    if (input.codePressed('BracketRight')) {
      this.cycleEntry(1)
      return
    }
    if (input.codePressed('BracketLeft')) {
      this.cycleEntry(-1)
      return
    }

    if (input.justPressed('jump')) {
      this.placeAtCursor()
      return
    }
    if (input.codePressed('Delete') || input.codePressed('Backspace')) {
      this.deleteAtCursor()
      return
    }

    for (const tool of TOOL_DEFS) {
      if (tool.key !== null && input.codePressed(tool.key)) {
        this.activateTool(tool)
        return
      }
    }

    if (input.codePressed('KeyT')) {
      this.cycleTerrainBrush(
        input.codeDown('ShiftLeft') || input.codeDown('ShiftRight') ? -1 : 1,
      )
      return
    }
    if (input.codePressed('KeyN')) {
      this.beginRename()
      return
    }
    if (input.codePressed('Comma')) {
      this.cycleTheme(-1)
      return
    }
    if (input.codePressed('Period')) {
      this.cycleTheme(1)
      return
    }
  }

  /** Printable keys, only while the inline name field has focus. */
  private onKeyDown = (e: KeyboardEvent): void => {
    if (this.nameEdit === null) return
    try {
      e.preventDefault()
      if (e.key === 'Enter') {
        this.commitRename()
        return
      }
      if (e.key === 'Escape') {
        this.nameEdit = null
        return
      }
      if (e.key === 'Backspace') {
        this.nameEdit = this.nameEdit.slice(0, -1)
        return
      }
      if (e.key.length === 1 && this.nameEdit.length < MAX_NAME)
        this.nameEdit += e.key
    } catch (err) {
      console.warn('[editor] name entry failed', err)
      this.nameEdit = null
    }
  }

  // ── palette / tool selection ──────────────────────────────────────────

  /**
   * Make `tool` the active one.
   *
   * Selecting a brush is also what decides what the *rectangle fill* lays down,
   * so the two have to be set together. They used to be maintained separately:
   * the palette set `fillMode` when you picked a cell, and the tool shortcuts
   * (`P` for the object brush, `T` for a terrain brush) did not. So "load the
   * object brush, then fill a rectangle" painted terrain, while the palette was
   * on the terrain tab -- silently, and in a way that looked like the fill tool
   * was broken. `fillMode` is context state, so it belongs here too.
   */
  private activateTool(tool: Tool): void {
    if (tool.id === 'object' && this.brush.objectDef === null) {
      this.toastMsg('PICK AN OBJECT FIRST')
      return
    }
    this.handTool = false
    this.tool = tool
    if (tool.kind === 'terrain') this.brush.fillMode = 'terrain'
    else if (tool.id === 'object') this.brush.fillMode = 'object'
  }

  private selectCategory(category: PaletteCategory): void {
    this.category = category
    const first = paletteEntries(this.category)[0]
    if (first !== undefined) this.selectEntry(first)
  }

  private selectEntry(entry: PaletteEntry): void {
    this.selectedIndex = null
    this.selectedKey = entry.key
    if (entry.kind === 'terrain') {
      this.activateTool(toolById(entry.toolId ?? 'ground'))
      this.brush.terrain = entry.terrain
      return
    }
    this.brush.objectDef = entry.def
    this.activateTool(toolById('object'))
  }

  /** Load an entry by key, switching to the tab it lives on. */
  private adoptKey(category: PaletteCategory, key: string): void {
    this.category = category
    const list = paletteEntries(category)
    const index = list.findIndex((e) => e.key === key)
    if (index < 0) return
    const entry = list[index]
    if (entry !== undefined) this.selectEntry(entry)
  }

  private selectIndex(index: number): void {
    const list = paletteEntries(this.category)
    if (list.length === 0) return
    const clamped = Math.max(0, Math.min(list.length - 1, index))
    const entry = list[clamped]
    if (entry === undefined) return
    this.selectEntry(entry)
    this.toastMsg(clip(entry.name))
  }

  private cycleEntry(dir: number): void {
    const entries = paletteEntries(this.category)
    if (entries.length === 0) return
    const current =
      this.selectedKey === null
        ? -1
        : entries.findIndex((entry) => entry.key === this.selectedKey)
    const next =
      current < 0 ? 0 : (current + dir + entries.length) % entries.length
    this.selectIndex(next)
  }

  private pickSlot(slot: number): void {
    const entry = entryForSlot(paletteEntries(this.category), slot)
    if (entry === null) {
      this.toastMsg(`SLOT ${slot} EMPTY`)
      return
    }
    this.selectEntry(entry)
    this.toastMsg(clip(entry.name))
  }

  private cycleTerrainBrush(dir: number): void {
    const list = paletteEntries('terrain')
    if (list.length === 0) return
    const current = list.findIndex((e) => e.terrain === this.brush.terrain)
    const next = (Math.max(0, current) + dir + list.length) % list.length
    this.adoptKey('terrain', list[next]?.key ?? `t:${Terrain.GROUND}`)
  }

  // ── editing ───────────────────────────────────────────────────────────

  private applyTool(tool: Tool, tx: number, ty: number): boolean {
    if (tool.id === 'erase') this.applyTool(toolById('object-erase'), tx, ty)
    if (!tool.canApply(this.level, tx, ty, this.brush)) return false
    if (tool.mutates) this.ensureStroke()
    const ok = tool.apply(this.level, tx, ty, this.brush)
    this.consumeNote()
    if (!ok) return false
    this.dirty = true
    this.consumePick(tool)
    return true
  }

  /**
   * Show whatever message the last tool wrote, if any.
   *
   * Drained even when the tool declined: the message a declined tool writes is
   * the reason it declined ("BLOCK IS FULL"), which is exactly what the user
   * needs to hear.
   */
  private consumeNote(): void {
    const note = this.brush.note
    this.brush.note = null
    if (note !== null) this.toastMsg(note)
  }

  /** The eyedropper writes into the context; turn that into a new brush. */
  private consumePick(tool: Tool): void {
    const pick = this.brush.pick
    const pickedObject = pick.object
    const pickedTerrain = pick.terrain
    pick.object = null
    pick.terrain = null
    if (pickedObject !== null) {
      const def = OBJECT_BY_ID.get(pickedObject)
      if (def !== undefined) {
        this.adoptKey(def.category, `o:${def.id}`)
        this.toastMsg(clip(def.name.toUpperCase()))
        return
      }
    }
    if (pickedTerrain === null) return
    const terrainTool = TOOL_FOR_TERRAIN.get(pickedTerrain)
    if (terrainTool !== undefined) {
      this.adoptKey('terrain', `t:${pickedTerrain}`)
      this.toastMsg(clip(terrainTool.name.toUpperCase()))
      return
    }
    this.brush.terrain = pickedTerrain
    this.toastMsg('TERRAIN ' + terrainLabel(pickedTerrain))
    if (tool.id !== 'eyedrop') this.brush.fillMode = 'terrain'
  }

  /**
   * Paint along the line from the previous tile to this one, so a fast drag
   * leaves no gaps. Bresenham, with a hard iteration cap in case a caller ever
   * hands us a wild coordinate pair.
   */
  private paintSegment(
    tool: Tool,
    x0: number,
    y0: number,
    x1: number,
    y1: number,
  ): void {
    // Tiles off the course are skipped rather than clamped, so a drag that
    // runs past the edge does not smear along the border.
    lineTiles(x0, y0, x1, y1, (tx, ty) => {
      if (tx < 0 || ty < 0 || tx >= this.level.width || ty >= this.level.height)
        return
      this.applyTool(tool, tx, ty)
    })
  }

  private placeAtCursor(): void {
    const hover = this.hoverTile
    if (!hover.inside) return
    if (this.tool.id === 'fill' || this.tool.id === 'move') {
      this.toastMsg('DRAG WITH ' + this.tool.name.toUpperCase())
      return
    }
    this.applyTool(this.tool, hover.tx, hover.ty)
    this.endStroke()
  }

  private deleteAtCursor(): void {
    const hover = this.hoverTile
    if (!hover.inside) return
    const hit = objectAtTile(this.level, hover.tx, hover.ty)
    if (hit === null) {
      this.toastMsg('NO OBJECT HERE')
      return
    }
    const index = placementIndexOf(this.level, hit)
    if (index < 0) return
    this.ensureStroke()
    this.level.removeObject(index)
    this.endStroke()
    this.dirty = true
    this.toastMsg('REMOVED ' + clip(hit.def.name.toUpperCase()))
  }

  private pickAt(tx: number, ty: number, preferTerrain: boolean): void {
    this.brush.preferTerrain = preferTerrain
    this.applyTool(toolById('eyedrop'), tx, ty)
    this.brush.preferTerrain = false
  }

  private beginRename(): void {
    this.nameEdit = this.level.data.name
    this.inputCanvas.focus?.()
  }

  private commitRename(): void {
    const name = (this.nameEdit ?? '').trim().slice(0, MAX_NAME)
    this.nameEdit = null
    if (name.length === 0 || name === this.level.data.name) return
    this.ensureStroke()
    this.level.data.name = name
    this.endStroke()
    this.dirty = true
    // Renaming is a save, not just a label: the course is filed under its name,
    // so a rename the user cannot see the effect of would look like data loss
    // when the selector still lists the old one.
    const save = this.persist()
    this.reportSave(save)
    this.toastSave(save)
  }

  private cycleTheme(dir: number): void {
    const current = this.level.data.theme
    const at = Math.max(0, THEMES.indexOf(current))
    const next =
      THEMES[(at + dir + THEMES.length) % THEMES.length] ?? 'overworld'
    if (next === current) return
    this.ensureStroke()
    const data = this.snapshot()
    data.theme = next
    this.rebuild(data)
    this.endStroke()
    this.toastMsg('THEME ' + next.toUpperCase())
  }

  // ── pointer plumbing ──────────────────────────────────────────────────

  private attachDom(): void {
    const c = this.inputCanvas
    c.addEventListener('pointerdown', this.onPointerDown)
    c.addEventListener('pointermove', this.onPointerMove)
    c.addEventListener('pointerup', this.onPointerUp)
    c.addEventListener('pointercancel', this.onPointerCancel)
    c.addEventListener('lostpointercapture', this.onLostPointerCapture)
    c.addEventListener('pointerleave', this.onPointerLeave)
    c.addEventListener('wheel', this.onWheel, { passive: false })
    c.addEventListener('contextmenu', this.onContextMenu)
    c.addEventListener('dragstart', this.onDragStart)
    window.addEventListener('keydown', this.onKeyDown)
    window.addEventListener('blur', this.onWindowBlur)
  }

  private detachDom(): void {
    const c = this.inputCanvas
    c.removeEventListener('pointerdown', this.onPointerDown)
    c.removeEventListener('pointermove', this.onPointerMove)
    c.removeEventListener('pointerup', this.onPointerUp)
    c.removeEventListener('pointercancel', this.onPointerCancel)
    c.removeEventListener('lostpointercapture', this.onLostPointerCapture)
    c.removeEventListener('pointerleave', this.onPointerLeave)
    c.removeEventListener('wheel', this.onWheel)
    c.removeEventListener('contextmenu', this.onContextMenu)
    c.removeEventListener('dragstart', this.onDragStart)
    window.removeEventListener('keydown', this.onKeyDown)
    window.removeEventListener('blur', this.onWindowBlur)
  }

  /**
   * Framebuffer coordinates, from a client point on the input surface. Works
   * for any CSS scale, including a non-integer one: the app may present the
   * 320x180 buffer stretched across a full-width canvas.
   */
  private toLocal(clientX: number, clientY: number): { x: number; y: number } {
    const box = this.inputCanvas.getBoundingClientRect()
    const sx = box.width > 0 ? box.width / VIEW_W : 1
    const sy = box.height > 0 ? box.height / VIEW_H : 1
    return {
      x: Math.floor((clientX - box.left) / sx),
      y: Math.floor((clientY - box.top) / sy),
    }
  }

  private inView(p: { x: number; y: number }): boolean {
    return (
      p.x >= VIEW.x &&
      p.x < VIEW.x + VIEW.w &&
      p.y >= VIEW.y &&
      p.y < VIEW.y + VIEW.h
    )
  }

  /**
   * Which tile a point on the level view is over.
   *
   * This used to be pure arithmetic — `cam + (p - VIEW.origin)`, divided by
   * TILE — and that was exact under the orthographic camera it was written for.
   * The 3D renderer is now perspective and tilted, so a straight line of screen
   * pixels maps to a *curve* of world tiles, and the arithmetic is wrong: it
   * drifts by a tile at the bottom of the view and misses entirely toward the
   * edges. Nothing throws, and the cursor is drawn by the same wrong maths, so the
   * highlight agrees with the hit test and the mistake is invisible -- you just
   * paint terrain somewhere other than where you clicked.
   *
   * The fix is to un-project: the renderer already knows the camera, so it can
   * un-project the pointer onto the play plane, which is exact for any
   * projection. `worldAt` in `src/engine3d/framing.ts`.
   */
  private tileAtScreen(p: { x: number; y: number }): {
    tx: number
    ty: number
  } {
    const world = this.renderer3d.worldAt(p.x - VIEW.x, p.y - VIEW.y)
    return { tx: Math.floor(world.x / TILE), ty: Math.floor(world.y / TILE) }
  }

  private trackPointer(p: { x: number; y: number }): void {
    const tile = this.tileAtScreen(p)
    this.hoverTile = { tx: tile.tx, ty: tile.ty, inside: this.inView(p) }
  }

  /** Release any lock left by an older hot-reloaded version of the shell. */
  private exitPointerLock(): void {
    try {
      if (document.pointerLockElement !== null) void document.exitPointerLock()
    } catch {
      /* pointer lock is a nicety, not a requirement */
    }
  }

  private onPointerDown = (e: PointerEvent): void => {
    try {
      e.preventDefault()
      this.exitPointerLock()
      const p = this.toLocal(e.clientX, e.clientY)
      this.trackPointer(p)

      if (e.pointerType === 'touch') {
        this.touchPointers.set(e.pointerId, {
          x: e.clientX,
          y: e.clientY,
        })
        // Second finger: this is a pan, not a stroke. A third is ignored; the
        // gesture follows the first two.
        if (this.touchPointers.size === 2) {
          this.beginTouchPan()
          return
        }
        if (this.touchPointers.size > 2) return
      }

      if (!this.inView(p)) return
      const selected = this.selectedObject()
      const handle = selected?.def.resize ? this.resizeHandle(selected) : null
      // A fingertip is wider than a cursor, so the grab radius grows on touch.
      const grab = e.pointerType === 'touch' ? 11 : 4
      if (
        !this.handTool &&
        selected &&
        handle &&
        Math.abs(p.x - handle.x) <= grab &&
        Math.abs(p.y - handle.y) <= grab
      ) {
        this.resizeDrag = {
          pointerId: e.pointerId,
          x: p.x,
          y: p.y,
          size:
            selected.def.resize === 'height' ? selected.def.h : selected.def.w,
          axis: selected.def.resize!,
        }
        this.inputCanvas.setPointerCapture(e.pointerId)
        return
      }
      if (this.handTool || e.button === 1 || e.altKey) {
        this.panDrag = {
          ...p,
          camX: this.cam.x,
          camY: this.cam.y,
          pointerId: e.pointerId,
        }
        this.inputCanvas.setPointerCapture(e.pointerId)
        return
      }
      this.beginLevelDrag(e)
    } catch (err) {
      console.warn('[editor] pointer down failed', err)
    }
  }

  private beginLevelDrag(e: PointerEvent): void {
    const hover = this.hoverTile
    const alt =
      e.altKey ||
      this.input.codeDown('AltLeft') ||
      this.input.codeDown('AltRight')
    const shift = e.shiftKey

    // Right button: eyedropper, never a drag.
    if (e.button === 2) {
      this.pickAt(hover.tx, hover.ty, alt)
      return
    }

    let tool =
      e.button === 1 || alt
        ? toolById('erase')
        : shift
          ? toolById('object-erase')
          : this.tool

    const tx = clamp(hover.tx, 0, this.level.width - 1)
    const ty = clamp(hover.ty, 0, this.level.height - 1)
    const existing = objectAtTile(this.level, tx, ty)
    if (
      existing &&
      (tool.id === 'move' || (tool.id === 'object' && existing.def.resize))
    ) {
      this.selectedIndex = placementIndexOf(this.level, existing)
      tool = toolById('move')
    }
    const drag: DragState = {
      pointerId: e.pointerId,
      mode: 'paint',
      toolId: tool.id,
      startTx: tx,
      startTy: ty,
      lastTx: tx,
      lastTy: ty,
      curTx: tx,
      curTy: ty,
      originTx: tx,
      originTy: ty,
      moveIndex: -1,
      moveObj: null,
      moveW: 1,
      moveH: 1,
    }

    if (tool.id === 'fill') {
      drag.mode = 'fill'
    } else if (tool.id === 'move') {
      const hit = objectAtTile(this.level, tx, ty)
      if (hit === null) {
        this.toastMsg('NOTHING TO MOVE HERE')
        return
      }
      drag.mode = 'move'
      drag.moveIndex = placementIndexOf(this.level, hit)
      drag.moveObj = hit
      drag.moveW = hit.def.w
      drag.moveH = hit.def.h
      drag.originTx = hit.tx
      drag.originTy = hit.ty
    }

    try {
      this.inputCanvas.setPointerCapture(e.pointerId)
    } catch {
      /* capture is an optimisation, not a requirement */
    }
    this.drag = drag
    this.strokeStartedAt = performance.now()
    if (drag.mode === 'paint') {
      // Select what the click *placed*. A drop that loaded a block adds no
      // placement, so the last object in the list would be an unrelated one —
      // the goal, usually — and the selection frame would jump to it.
      const before = this.level.data.objects.length
      if (
        this.applyTool(tool, tx, ty) &&
        tool.id === 'object' &&
        this.level.data.objects.length > before
      ) {
        this.selectedIndex = this.level.data.objects.length - 1
      }
    }
  }

  private onPointerMove = (e: PointerEvent): void => {
    try {
      const p = this.toLocal(e.clientX, e.clientY)
      this.trackPointer(p)
      if (e.pointerType === 'touch' && this.touchPointers.has(e.pointerId)) {
        this.touchPointers.set(e.pointerId, {
          x: e.clientX,
          y: e.clientY,
        })
        if (this.touchPan !== null) {
          this.applyTouchPan()
          return
        }
      }
      if (this.resizeDrag !== null) {
        const drag = this.resizeDrag
        if (e.pointerId !== drag.pointerId) return
        const delta = drag.axis === 'height' ? drag.y - p.y : p.x - drag.x
        this.resizeSelected(drag.size + Math.round(delta / TILE))
        return
      }
      if (this.panDrag !== null) {
        const pan = this.panDrag
        if (e.pointerId !== pan.pointerId) return
        if (e.buttons === 0 && e.pointerType !== 'touch') {
          this.abandonDrag()
          return
        }
        this.cam.x = Math.round(pan.camX - (p.x - pan.x))
        this.cam.y = Math.round(pan.camY - (p.y - pan.y))
        this.cam.x = clamp(
          this.cam.x,
          0,
          Math.max(0, this.level.width * TILE - VIEW.w),
        )
        this.cam.y = clamp(
          this.cam.y,
          0,
          Math.max(0, this.level.height * TILE - VIEW.h),
        )
        return
      }
      const drag = this.drag
      if (drag === null) return
      if (e.pointerId !== drag.pointerId) return
      // Button released somewhere we never saw: abandon the stroke.
      if (e.buttons === 0 && e.pointerType !== 'touch') {
        this.endDrag(false)
        return
      }
      const tile = this.tileAtScreen(p)
      const tx = clamp(tile.tx, 0, this.level.width - 1)
      const ty = clamp(tile.ty, 0, this.level.height - 1)
      drag.curTx = tx
      drag.curTy = ty
      if (tx === drag.lastTx && ty === drag.lastTy) return
      if (
        drag.mode === 'paint' &&
        !(drag.toolId === 'object' && this.brush.objectDef?.resize)
      ) {
        this.paintSegment(
          toolById(drag.toolId),
          drag.lastTx,
          drag.lastTy,
          tx,
          ty,
        )
      }
      drag.lastTx = tx
      drag.lastTy = ty
    } catch (err) {
      console.warn('[editor] pointer move failed', err)
    }
  }

  private onPointerUp = (e: PointerEvent): void => {
    this.releaseTouchPointer(e)
    if (this.resizeDrag !== null) {
      const id = this.resizeDrag.pointerId
      this.resizeDrag = null
      this.releaseCapture(id)
      this.endStroke()
    }
    if (this.panDrag !== null) {
      const id = this.panDrag.pointerId
      this.panDrag = null
      if (this.inputCanvas.hasPointerCapture(id))
        this.inputCanvas.releasePointerCapture(id)
    }
    try {
      this.endDrag(true)
    } catch (err) {
      console.warn('[editor] pointer up failed', err)
    }
  }

  private onPointerCancel = (e: PointerEvent): void => {
    this.releaseTouchPointer(e)
    try {
      this.abandonDrag()
    } catch (err) {
      console.warn('[editor] pointer cancel failed', err)
    }
  }

  /** Forget a lifted touch pointer, ending the pan when fewer than two remain. */
  private releaseTouchPointer(e: PointerEvent): void {
    if (!this.touchPointers.delete(e.pointerId)) return
    if (this.touchPointers.size < 2) this.touchPan = null
  }

  /**
   * Start a two-finger pan.
   *
   * Whatever the first finger had started is closed first. A stroke that had
   * only just begun — a fresh touch, still on its origin tile, a fraction of a
   * second old — is the classic "I meant to pan and put my fingers down a beat
   * apart" dot, and is reverted rather than kept. A stroke that got somewhere is
   * committed, because undoing real work to start a pan would be worse.
   */
  private beginTouchPan(): void {
    const drag = this.drag
    if (drag !== null) {
      const fresh =
        drag.lastTx === drag.originTx &&
        drag.lastTy === drag.originTy &&
        performance.now() - this.strokeStartedAt < 220
      if (fresh) this.revertOpenStroke()
      else this.endDrag(true)
    }
    if (this.resizeDrag !== null) {
      const id = this.resizeDrag.pointerId
      this.resizeDrag = null
      this.releaseCapture(id)
      this.endStroke()
    }
    this.panDrag = null

    const [a, b] = [...this.touchPointers.values()]
    if (a === undefined || b === undefined) return
    const p1 = this.toLocal(a.x, a.y)
    const p2 = this.toLocal(b.x, b.y)
    this.touchPan = {
      camX: this.cam.x,
      camY: this.cam.y,
      midX: (p1.x + p2.x) / 2,
      midY: (p1.y + p2.y) / 2,
    }
    // Keep both fingers captured, so the pan survives a drag past the edge.
    for (const id of this.touchPointers.keys()) {
      try {
        this.inputCanvas.setPointerCapture(id)
      } catch {
        /* capture is an optimisation, not a requirement */
      }
    }
  }

  /** Move the camera by the midpoint delta of the two panning fingers. */
  private applyTouchPan(): void {
    const pan = this.touchPan
    if (pan === null || this.touchPointers.size < 2) return
    const [a, b] = [...this.touchPointers.values()]
    if (a === undefined || b === undefined) return
    const p1 = this.toLocal(a.x, a.y)
    const p2 = this.toLocal(b.x, b.y)
    const midX = (p1.x + p2.x) / 2
    const midY = (p1.y + p2.y) / 2
    this.cam.x = Math.round(pan.camX - (midX - pan.midX))
    this.cam.y = Math.round(pan.camY - (midY - pan.midY))
    this.clampCamera()
  }

  /**
   * Put back the level a stroke had started to change, without an undo entry.
   *
   * The stroke's snapshot is the state before its first mutation, so restoring
   * it is exact. The stroke is dropped first so `endDrag` cannot record it.
   */
  private revertOpenStroke(): void {
    const snap = this.openSnapshot
    this.dropStroke()
    if (this.drag !== null) this.endDrag(false)
    if (snap !== null) this.rebuild(snap)
  }

  /**
   * The browser took the capture back for a reason we did not choose.
   *
   * This is the event that makes the difference between "the editor knows it is
   * not dragging" and "the editor thinks it is". Capture is released implicitly
   * in several situations -- the element is hidden or removed, the browser
   * reassigns it, a modality change -- and if the drag state is not closed when
   * that happens, the editor sits there with a stroke it believes is still open.
   * The next stroke then coalesces into the abandoned one, and undo removes both.
   *
   * It also fires when *we* release, which is why `endDrag` clears the drag
   * first: by the time this runs, there is nothing to abandon.
   */
  private onLostPointerCapture = (): void => {
    this.abandonDrag()
  }

  /**
   * Alt-tabbing or otherwise losing focus mid-drag.
   *
   * `pointerup` will eventually arrive when the user comes back and releases,
   * but nothing arrives if they release while the window is *not* focused -- and
   * a drag left open across a focus change is the same dangling stroke as above.
   */
  private onWindowBlur = (): void => {
    this.abandonDrag()
  }

  private onPointerLeave = (): void => {
    if (this.drag !== null) return
    this.hoverTile = {
      tx: this.hoverTile.tx,
      ty: this.hoverTile.ty,
      inside: false,
    }
  }

  private onWheel = (e: WheelEvent): void => {
    try {
      // Without this the page scrolls under the editor.
      e.preventDefault()
      const p = this.toLocal(e.clientX, e.clientY)
      this.trackPointer(p)
      const dir = e.deltaY > 0 ? 1 : -1
      this.cycleEntry(dir)
    } catch (err) {
      console.warn('[editor] wheel failed', err)
    }
  }

  /** Kill the browser menu: right click is the eyedropper here. */
  private onContextMenu = (e: Event): void => {
    e.preventDefault()
  }

  private onDragStart = (e: Event): void => {
    e.preventDefault()
  }

  private endDrag(commit: boolean): void {
    const drag = this.drag
    // Cleared *before* the capture is released. Releasing fires
    // `lostpointercapture` synchronously, and the handler for that must see a
    // finished drag rather than re-enter this method.
    this.drag = null
    if (drag === null) return
    this.releaseCapture(drag.pointerId)
    if (commit) {
      if (drag.mode === 'fill') this.commitFill(drag)
      else if (drag.mode === 'move') this.commitMove(drag)
    }
    this.endStroke()
  }

  /**
   * Give the pointer back, if we still have it.
   *
   * `releasePointerCapture` throws when the element does not hold the capture,
   * so the check is not defensive noise: capture is released implicitly by the
   * browser on `pointerup`, on `pointercancel` and when an element leaves the
   * DOM, which means "do we still have it" is genuinely not always true. The
   * throw is caught anyway, because a diagnostic that can throw is not one.
   */
  private releaseCapture(pointerId: number): void {
    try {
      if (this.inputCanvas.hasPointerCapture?.(pointerId) !== true) return
      this.inputCanvas.releasePointerCapture(pointerId)
    } catch {
      /* the browser already gave it back */
    }
  }

  /**
   * Stop a stroke without keeping it, for when the drag was not the player's
   * idea of an ending: the editor closing under them, the window losing focus,
   * or the browser taking the capture back on its own.
   *
   * The paint work already done stays in the level -- a brush stroke mutates as
   * it is dragged, it is not buffered -- so what is abandoned is the *undo
   * record*, not the edit. That is the right way round when closing the editor,
   * where the edit is about to be saved and an undo nobody can reach is noise.
   */
  private abandonDrag(): void {
    if (this.resizeDrag !== null) {
      const id = this.resizeDrag.pointerId
      this.resizeDrag = null
      this.releaseCapture(id)
      this.endStroke()
    }
    if (this.panDrag !== null) {
      const id = this.panDrag.pointerId
      this.panDrag = null
      if (this.inputCanvas.hasPointerCapture(id))
        this.inputCanvas.releasePointerCapture(id)
    }
    if (this.drag === null) return
    this.endDrag(false)
  }

  private commitFill(drag: DragState): void {
    const x0 = drag.startTx
    const y0 = drag.startTy
    const x1 = drag.curTx
    const y1 = drag.curTy
    // Dry run first: a fill that would change nothing should not burn an undo
    // entry (or repaint the world twice for nothing).
    if (fillRect(this.level, x0, y0, x1, y1, this.brush, true) === 0) {
      this.toastMsg('NOTHING TO FILL')
      return
    }
    this.ensureStroke()
    const n = fillRect(this.level, x0, y0, x1, y1, this.brush, false)
    if (n === 0) return
    this.dirty = true
    this.toastMsg(`FILLED ${n}`)
  }

  private commitMove(drag: DragState): void {
    const obj = drag.moveObj
    if (obj === null || drag.moveIndex < 0) return
    const tx = clamp(drag.curTx, 0, this.level.width - drag.moveW)
    const ty = clamp(drag.curTy, 0, this.level.height - drag.moveH)
    if (tx === obj.tx && ty === obj.ty) return
    if (!fits(this.level, tx, ty, drag.moveW, drag.moveH, obj)) {
      this.toastMsg('BLOCKED')
      return
    }
    this.ensureStroke()
    if (moveObject(this.level, obj, drag.moveIndex, tx, ty)) {
      this.dirty = true
      this.toastMsg('MOVED ' + clip(obj.def.name.toUpperCase()))
    }
  }

  private selectedObject(): PlacedObject | null {
    if (this.selectedIndex === null) return null
    return (
      this.level.objects.find(
        (o) =>
          o.alive && placementIndexOf(this.level, o) === this.selectedIndex,
      ) ?? null
    )
  }

  private resizeHandle(object: PlacedObject): { x: number; y: number } {
    return {
      x: VIEW.x + (object.tx + object.def.w) * TILE - this.cam.x,
      y:
        VIEW.y +
        (object.ty + (object.def.resize === 'height' ? 0 : object.def.h / 2)) *
          TILE -
        this.cam.y,
    }
  }

  private resizeSelected(size: number): void {
    const object = this.selectedObject()
    if (!object?.def.resize) return
    const current = object.def.resize === 'height' ? object.def.h : object.def.w
    if (current === size) return
    const placement = resizedPlacement(this.level, object, size)
    if (!placement) {
      this.toastMsg('NOT ENOUGH ROOM')
      return
    }
    if (placement.props?.[object.def.resize] === current) return
    this.ensureStroke()
    const data = this.snapshot()
    data.objects[placementIndexOf(this.level, object)] = placement
    this.rebuild(data)
    this.dirty = true
  }

  // ── rendering ─────────────────────────────────────────────────────────

  private render(): void {
    const ctx = this.ctx
    // The level view is *not* painted here. This canvas is the interface layer
    // and it is transparent over the WebGL scene, so filling the whole buffer
    // would bury the 3D view under a flat rectangle. Only the chrome strips get
    // their own backgrounds; the Canvas overlay stays transparent.
    ctx.clearRect(0, 0, VIEW_W, VIEW_H)
    ctx.save()
    ctx.beginPath()
    ctx.rect(VIEW.x, VIEW.y, VIEW.w, VIEW.h)
    ctx.clip()
    // The 3D scene fills exactly the rect the chrome does not cover, which is
    // the same world window the old `ctx.translate(VIEW.x, VIEW.y)` produced.
    this.renderer3d.setViewport({ x: VIEW.x, y: VIEW.y, w: VIEW.w, h: VIEW.h })
    try {
      this.renderer3d.renderLevel(this.level, this.cam, {
        showGrid: true,
        editorCursor: this.hoverCursor(),
        // The one view where a hidden block is not a secret: you cannot place
        // what you cannot see, and the badge on it has to land on a block.
        revealHidden: true,
      })
    } catch (e) {
      console.warn('[editor] level render failed', e)
      fillBox(ctx, VIEW_LOCAL, '#0b0b16')
      drawTextCentre(
        ctx,
        'RENDER UNAVAILABLE',
        VIEW_LOCAL.w / 2,
        VIEW_LOCAL.h / 2,
        '#ff6060',
      )
    }
    try {
      const overlay: OverlayView = {
        view: VIEW_LOCAL,
        camX: this.cam.x,
        camY: this.cam.y,
        startTx: this.level.data.startX,
        startTy: this.level.data.startY,
        cursor: this.hoverCursor(),
        drag: this.drag,
        badges: payloadBadges(this.level.objects),
        time: this.now,
      }
      ctx.save()
      ctx.translate(VIEW.x, VIEW.y)
      drawOverlay(ctx, overlay)
      ctx.restore()
    } catch (e) {
      console.warn('[editor] overlay failed', e)
    }
    ctx.restore()
    const selected = this.selectedObject()
    if (selected) {
      ctx.save()
      ctx.beginPath()
      ctx.rect(VIEW.x, VIEW.y, VIEW.w, VIEW.h)
      ctx.clip()
      ctx.strokeStyle = '#ffe34c'
      ctx.lineWidth = 1
      ctx.strokeRect(
        VIEW.x + selected.tx * TILE - this.cam.x,
        VIEW.y + selected.ty * TILE - this.cam.y,
        selected.def.w * TILE,
        selected.def.h * TILE,
      )
      if (selected.def.resize) {
        const handle = this.resizeHandle(selected)
        ctx.fillStyle = '#ffe34c'
        ctx.fillRect(handle.x - 3, handle.y - 3, 6, 6)
        ctx.strokeStyle = '#554400'
        ctx.strokeRect(handle.x - 3, handle.y - 3, 6, 6)
      }
      ctx.restore()
    }
    this.chrome?.update(
      this.chromeSource(),
      this.handTool,
      selected?.def ?? null,
    )
    const toast = this.toast
    if (toast !== null && this.now <= toast.until)
      drawToast(ctx, VIEW, toast.text)
  }

  /**
   * The hovered tile and its footprint, for the renderer's own cursor drawing
   * (green legal / red blocked, with marching ants). Null when the pointer is
   * over the chrome or off the course.
   */
  private hoverCursor(): CursorBox | null {
    const hover = this.hoverTile
    if (!hover.inside || this.handTool || this.panDrag !== null) return null
    const fp = this.tool.footprint(this.brush)
    return {
      tx: hover.tx,
      ty: hover.ty,
      w: fp.w,
      h: fp.h,
      ok: this.tool.canApply(this.level, hover.tx, hover.ty, this.brush),
    }
  }

  /**
   * Gather what the chrome needs to draw itself.
   *
   * Public because it is the editor's whole observable presentation state, and
   * because that makes it the honest thing for a test to read: what the status
   * line says and what the tool strip shows about *this* editor, not about a
   * hand-built stand-in.
   */
  chromeSource(): ChromeSource {
    const def: ObjectDef | null = this.brush.objectDef
    const selected = this.selectedEntry()
    const brushLabel =
      this.brush.fillMode === 'object' && def !== null
        ? def.name
        : terrainLabel(this.brush.terrain)
    const desiredName = this.nameEdit ?? this.level.data.name
    return {
      activeTool: this.tool,
      activeCategory: this.category,
      selectedKey: this.selectedKey,
      selectedHint: selected?.hint ?? '',
      brushObject: this.brush.fillMode === 'object' ? def : null,
      levelName: desiredName,
      theme: this.level.data.theme,
      brushLabel,
      warnings: this.issues.map((i) => i.text),
      undoDepth: this.undoStack.length,
      redoDepth: this.redoStack.length,
      editingName: this.nameEdit,
      copyAs:
        this.source?.builtIn === true
          ? resolveSaveName(desiredName, this.source)
          : null,
    }
  }

  private selectedEntry(): PaletteEntry | null {
    if (this.selectedKey === null) return null
    return (
      paletteEntries(this.category).find((e) => e.key === this.selectedKey) ??
      null
    )
  }

  private toastMsg(text: string): void {
    this.toast = { text, until: this.now + 1200 }
  }
}

// ── entry point ────────────────────────────────────────────────────────────

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

/**
 * Do two records of a course say the same thing?
 *
 * Both sides go through `cloneLevelData` so the comparison is over one shape.
 * That is not fussiness: `toData()` puts `author` second and `cloneLevelData`
 * puts it last, so comparing a snapshot's serialisation against `toData()`'s
 * directly reports a difference for every course that has an author -- which is
 * every shipped course -- and the check silently never fires for them.
 */
function sameLevel(a: LevelData, level: Level): boolean {
  return JSON.stringify(a) === JSON.stringify(cloneLevelData(level.toData()))
}

/** Toast strings are clipped to the framebuffer width. */
function clip(text: string): string {
  return clipText(text, MAX_CHARS - 2)
}

/** Is this canvas actually on screen, with a box the user can point at? */
function isOnScreen(el: HTMLCanvasElement): boolean {
  if (!el.isConnected) return false
  const box = el.getBoundingClientRect()
  return box.width > 0 && box.height > 0
}

/**
 * The canvas whose surface the user's pointer lands on.
 *
 * The shell may hand the editor an offscreen 320x180 buffer to draw into and
 * then blit it up to a larger display canvas. Pointer events only ever reach
 * the display canvas, so listening on the drawing buffer would leave the editor
 * completely dead to the mouse. Prefer the canvas we were given when it is on
 * screen, and otherwise fall back to the first visible canvas in the document.
 */
function resolveInputCanvas(canvas: HTMLCanvasElement): HTMLCanvasElement {
  if (isOnScreen(canvas)) return canvas
  const doc = canvas.ownerDocument
  if (doc === null) return canvas
  for (const el of doc.querySelectorAll('canvas')) {
    if (isOnScreen(el)) return el
  }
  return canvas
}

/**
 * Open the editor on `canvas`. The returned function tears it down and calls
 * `onClose`; calling it more than once (or after the app closed the editor
 * itself) is safe.
 *
 * The fourth argument is an options bag rather than the level itself, because a
 * session needs to know two things about its starting point: the level to edit
 * (`initialLevel`) and which library entry it came from (`source`, which is
 * what decides whether a save may overwrite it). `src/main.ts` is the only
 * caller.
 */
export function openEditor(
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  onClose: () => void,
  options: EditorOptions,
): { close: () => void; resume: () => void } {
  let closed = false
  const close = (): void => {
    if (closed) return
    closed = true
    try {
      onClose()
    } catch (e) {
      console.warn('[editor] onClose threw', e)
    }
  }

  let editor: Editor | null = null
  try {
    editor = new Editor(canvas, ctx, close, options)
    editor.start()
  } catch (e) {
    console.warn('[editor] could not open the editor', e)
    try {
      editor?.teardown()
    } catch {
      /* nothing left to clean up */
    }
    close()
    return { close, resume: () => {} }
  }

  return {
    resume: () => editor?.resume(),
    close: (): void => {
      try {
        editor?.teardown()
      } catch (e) {
        console.warn('[editor] teardown failed', e)
      }
      close()
    },
  }
}
